// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type { TFunction } from 'i18next';
import { AdvisoryBankProductType } from '@shared/data-access/types/brokerage-types';

const PRODUCT_TYPE_FALLBACK_KEY: Record<AdvisoryBankProductType, string> = {
  [AdvisoryBankProductType.Account]: 'bank-accounts',
  [AdvisoryBankProductType.Card]: 'cards',
  [AdvisoryBankProductType.Loan]: 'mortgages-and-loans',
  [AdvisoryBankProductType.Portfolio]: 'investments',
};

const KEY_BASE = 'aggregator.product-type-value';

const normalizeKey = (raw: string): string =>
  raw.toLowerCase().replace(/\s+/g, '-');

/**
 * Resolves the product type label from a single i18n key base. If the translation
 * key exists it is used; otherwise the value is rendered as-is.
 *
 * Normalization maps API values (e.g. "Pension plans", "Debit") to the JSON key
 * format (lowercase, hyphens: "pension-plans", "debit") so the key exists in the
 * bundle.
 *
 * @param productType - Discriminant (AdvisoryBankProductType).
 * @param type - Optional sub-type from the product (e.g. "Debit", "Stocks").
 * @param t - I18next TFunction (e.g. from useTranslation().t) to resolve the key.
 */
export const getProductTypeLabel = (
  productType: AdvisoryBankProductType,
  type: string | null | undefined,
  t: TFunction,
): string => {
  const key = type
    ? `${KEY_BASE}.${normalizeKey(type)}`
    : `${KEY_BASE}.${PRODUCT_TYPE_FALLBACK_KEY[productType]}`;
  return t(key, { ns: 'aggregator', defaultValue: type ?? productType });
};
